// Add these type declarations at the top of the file
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

import React, { useState, useEffect, useRef } from "react";
import {
  Circle,
  Brain,
  Send,
  X,
  MessageCircle,
  Calendar,
  ThumbsUp,
  ThumbsDown,
  Heart,
  Mic,
  Volume2,
  VolumeX,
  CalendarDays,
} from "lucide-react";

// Data structure to store user information
interface UserData {
  name: string;
  email: string;
  phone?: string;
  timestamp: string;
}

interface Message {
  text: string;
  sender: "user" | "bot";
  timestamp: Date;
  reactions?: string[];
}

interface UserPreference {
  topic: string;
  sentiment: "positive" | "negative" | "neutral";
  frequency: number;
}

interface FloatingChatProps {
  onMetricsUpdate?: (metrics: any) => void;
}

interface VoiceState {
  isRecording: boolean;
  isSpeaking: boolean;
  isSupported: boolean;
  error: string | null;
}

// Add a new interface for message playback state
interface MessagePlayback {
  messageId: number;
  isPlaying: boolean;
}

const FloatingChat: React.FC<FloatingChatProps> = ({ onMetricsUpdate }) => {
  // Company details
  const companyName = "InnovateAI Technologies";
  const companyEmail = "automate@innovateaitechnologies.com";

  // Update the Calendly URL with proper parameters
  const calendlyUrl =
    "https://calendly.com/automate-innovateaitechnologies/30min";

  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      text: `Hi! Welcome to ${companyName}. I'm here to help you discover how our AI-powered solutions can transform your business processes. How can I assist you today?`,
      sender: "bot",
      timestamp: new Date(),
    },
  ]);

  const [currentMessage, setCurrentMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [collectingName, setCollectingName] = useState(false);
  const [collectingEmail, setCollectingEmail] = useState(false);
  const [collectingPhone, setCollectingPhone] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [userPreferences, setUserPreferences] = useState<UserPreference[]>([]);
  const [selectedMessageIndex, setSelectedMessageIndex] = useState<
    number | null
  >(null);
  const [hasBookedCall, setHasBookedCall] = useState(false);

  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const chatContainerRef = useRef<null | HTMLDivElement>(null);

  const [voiceState, setVoiceState] = useState<VoiceState>({
    isRecording: false,
    isSpeaking: false,
    isSupported:
      "webkitSpeechRecognition" in window || "SpeechRecognition" in window,
    error: null,
  });

  const recognitionRef = useRef<any>(null);
  const synthRef = useRef<SpeechSynthesis | null>(null);

  // Common FAQs as quick-select options
  const quickFaqs = [
    "What services do you offer?",
    "How can AI improve my business?",
    "What is workflow automation?",
    "Schedule a demo call",
  ];

  // Add state for message playback
  const [playingMessage, setPlayingMessage] = useState<MessagePlayback | null>(
    null
  );

  // Scroll to bottom when messages update
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle click outside to collapse chat if expanded and clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isExpanded &&
        chatContainerRef.current &&
        !chatContainerRef.current.contains(event.target as Node)
      ) {
        setIsMinimized(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  // Load user preferences from localStorage on initial render
  useEffect(() => {
    const savedPreferences = localStorage.getItem("innovateAI_userPreferences");
    if (savedPreferences) {
      try {
        setUserPreferences(JSON.parse(savedPreferences));
      } catch (e) {
        console.error("Failed to parse saved preferences:", e);
      }
    }

    // Also load user data if available
    const savedUserData = localStorage.getItem("innovateAI_userData");
    if (savedUserData) {
      try {
        setUserData(JSON.parse(savedUserData));
      } catch (e) {
        console.error("Failed to parse saved user data:", e);
      }
    }
  }, []);

  // Initialize Web Speech API
  useEffect(() => {
    if (voiceState.isSupported) {
      try {
        const SpeechRecognition =
          window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = false;
        recognitionRef.current.lang = "en-US";

        // Initialize speech synthesis and load voices
        synthRef.current = window.speechSynthesis;

        // Set up recognition event handlers with improved error handling
        recognitionRef.current.onresult = (event: any) => {
          try {
            const transcript = event.results[0][0].transcript;
            const confidence = event.results[0][0].confidence;

            // Only process input if confidence is above threshold
            if (confidence > 0.5) {
              if (isEnglish(transcript)) {
                setCurrentMessage(transcript);
                // Automatically send the message after voice recognition
                setTimeout(() => {
                  const newMessage = {
                    text: transcript,
                    sender: "user" as const,
                    timestamp: new Date(),
                  };
                  setMessages((prev) => [...prev, newMessage]);
                  setLoading(true);
                  processUserMessage(transcript);
                }, 500);
              } else {
                setVoiceState((prev) => ({
                  ...prev,
                  error: "Please speak in English only.",
                  isRecording: false,
                }));
              }
            } else {
              setVoiceState((prev) => ({
                ...prev,
                error: "Could not understand speech clearly. Please try again.",
                isRecording: false,
              }));
            }
          } catch (error) {
            console.error("Speech recognition result error:", error);
            setVoiceState((prev) => ({
              ...prev,
              error: "Error processing speech. Please try again.",
              isRecording: false,
            }));
          }
        };

        recognitionRef.current.onerror = (event: any) => {
          console.error("Speech recognition error:", event.error);
          let errorMessage = "Error recording voice. ";

          switch (event.error) {
            case "not-allowed":
              errorMessage += "Please enable microphone access.";
              break;
            case "network":
              errorMessage += "Please check your internet connection.";
              break;
            case "no-speech":
              errorMessage += "No speech detected. Please try again.";
              break;
            case "aborted":
              errorMessage += "Recording was aborted.";
              break;
            case "audio-capture":
              errorMessage += "No microphone was found.";
              break;
            default:
              errorMessage += "Please try again.";
          }

          setVoiceState((prev) => ({
            ...prev,
            error: errorMessage,
            isRecording: false,
          }));
        };

        recognitionRef.current.onend = () => {
          setVoiceState((prev) => ({ ...prev, isRecording: false }));
        };
      } catch (error) {
        console.error("Error initializing speech recognition:", error);
        setVoiceState((prev) => ({
          ...prev,
          isSupported: false,
          error: "Speech recognition is not supported in your browser.",
        }));
      }
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (error) {
          console.error("Error aborting recognition:", error);
        }
      }
      if (synthRef.current) {
        try {
          synthRef.current.cancel();
        } catch (error) {
          console.error("Error canceling synthesis:", error);
        }
      }
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleChat = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      setIsMinimized(false);
    } else {
      setIsMinimized(!isMinimized);
    }
  };

  const closeChat = () => {
    setIsMinimized(true);
  };

  const handleSendMessage = () => {
    if (!currentMessage.trim()) return;

    if (!isEnglish(currentMessage)) {
      setVoiceState((prev) => ({
        ...prev,
        error: "Please type in English only.",
      }));
      return;
    }

    const newMessage = {
      text: currentMessage,
      sender: "user" as const,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, newMessage]);
    setCurrentMessage("");
    setLoading(true);

    // Process user input based on current collection state
    if (collectingName) {
      handleNameInput(currentMessage);
    } else if (collectingEmail) {
      handleEmailInput(currentMessage);
    } else if (collectingPhone) {
      handlePhoneInput(currentMessage);
    } else {
      // Regular conversation flow
      setTimeout(() => {
        processUserMessage(currentMessage);
      }, 1000);
    }
  };

  const processUserMessage = (message: string) => {
    const lowerMessage = message.toLowerCase();

    // Check for name introduction patterns
    const namePatterns = [
      /(?:i am|i'm|my name is|this is|call me) (\w+)/i,
      /^(\w+) here$/i,
      /^hi,?\s+(?:i(?:\'|')?m\s+)?(\w+)$/i,
    ];

    let extractedName = null;
    for (const pattern of namePatterns) {
      const match = message.match(pattern);
      if (match && match[1]) {
        extractedName =
          match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        break;
      }
    }

    if (extractedName && !userData) {
      // Store the extracted name and ask for email
      setContactFormData((prev) => ({ ...prev, name: extractedName }));
      botResponse(
        `Nice to meet you, ${extractedName}! To better assist you, could you please share your email address?`
      );
      setCollectingEmail(true);
      return;
    }

    // Check if this contains emoji reactions
    if (containsEmoji(lowerMessage)) {
      handleEmojiMessage(lowerMessage);
      return;
    }

    // Check if user is confirming they've booked a call
    if (
      hasBookedCall &&
      (lowerMessage.includes("booked") ||
        lowerMessage.includes("scheduled") ||
        lowerMessage.includes("call") ||
        lowerMessage.includes("appointment") ||
        lowerMessage.includes("meeting"))
    ) {
      botResponse(
        "Perfect! Our team is looking forward to the call. In the meantime, feel free to explore our website or ask me any other questions about our AI solutions."
      );
      return;
    }

    // Check if this is a greeting
    if (
      lowerMessage.includes("hi") ||
      lowerMessage.includes("hello") ||
      lowerMessage.includes("hey")
    ) {
      if (!userData) {
        // Start collecting name if we don't have user data
        askForName();
      } else {
        const preferredTopics = getTopPreferences(2);
        let response = `Great to see you again, ${userData.name}! How can I assist you today?`;

        if (preferredTopics.length > 0) {
          response += ` Based on our previous conversations, would you like to know more about ${preferredTopics.join(
            " or "
          )}?`;
        }

        botResponse(response);
      }
    }
    // Check if the user is asking about contact information
    else if (
      lowerMessage.includes("contact") ||
      lowerMessage.includes("email") ||
      lowerMessage.includes("reach")
    ) {
      updateUserPreference("contact", "positive");
      botResponse(
        `You can reach our team at ${companyEmail} for any inquiries or support needs.`
      );
    }
    // Check if the user is interested in learning more
    else if (
      lowerMessage.includes("learn more") ||
      lowerMessage.includes("tell me more") ||
      lowerMessage.includes("interested") ||
      lowerMessage.includes("services")
    ) {
      updateUserPreference("services", "positive");

      if (!userData) {
        askForName();
      } else {
        botResponse(
          "Our AI-driven solutions can automate your business processes, saving time and reducing costs. We specialize in workflow automation, data analysis, and predictive analytics. Would you like to see a demo or discuss a specific use case?"
        );
      }
    }
    // Check if the user is asking about specific solutions
    else if (
      lowerMessage.includes("automation") ||
      lowerMessage.includes("workflow") ||
      lowerMessage.includes("process")
    ) {
      updateUserPreference("workflow", "positive");
      botResponse(
        "Our workflow automation solutions can save up to 85% of manual processing time and reduce errors by 95%. You can create custom workflows using our drag-and-drop builder that you saw earlier. Would you like to schedule a call to discuss how this could work for your business?"
      );
    }
    // Check if the user wants to schedule a call
    else if (
      lowerMessage.includes("call") ||
      lowerMessage.includes("schedule") ||
      lowerMessage.includes("book") ||
      lowerMessage.includes("appointment") ||
      lowerMessage.includes("demo") ||
      lowerMessage.includes("calendly")
    ) {
      updateUserPreference("scheduling", "positive");

      if (userData) {
        botResponse(
          `I'd be happy to help you schedule a call with our team, ${userData.name}. Here's our booking link: ${calendlyUrl}\n\nOnce you've scheduled the call, please let me know and I'll be happy to help you with any other questions!`
        );
      } else {
        botResponse(
          "I'd be happy to help you schedule a call. First, could you please share your name so we can personalize your experience?"
        );
        setCollectingName(true);
      }
    }
    // Handle feedback about the chat
    else if (
      lowerMessage.includes("great chat") ||
      lowerMessage.includes("helpful") ||
      lowerMessage.includes("thanks") ||
      lowerMessage.includes("thank you")
    ) {
      updateUserPreference("satisfaction", "positive");
      botResponse(
        "I'm glad I could be of help! Is there anything else you'd like to know about our AI solutions?"
      );
    }
    // Handle negative feedback
    else if (
      lowerMessage.includes("not helpful") ||
      lowerMessage.includes("useless") ||
      lowerMessage.includes("bad") ||
      lowerMessage.includes("terrible")
    ) {
      updateUserPreference("satisfaction", "negative");
      botResponse(
        "I'm sorry I haven't been helpful. Could you tell me more specifically what you're looking for so I can better assist you?"
      );
    }
    // Default response for other queries with personalization based on preferences
    else {
      const preferredTopics = getTopPreferences(1);
      let defaultResponse =
        "Thanks for your message. Our AI solutions can help automate various business processes, increase efficiency, and reduce costs.";

      if (preferredTopics.length > 0) {
        defaultResponse += ` Based on your interests, you might want to explore our ${preferredTopics[0]} solutions. Would you like to learn more about this?`;
      } else {
        defaultResponse +=
          " Would you like to learn more about any specific area of our services?";
      }

      botResponse(defaultResponse);
    }
  };

  const containsEmoji = (message: string): boolean => {
    // Check for common emojis or text representations
    return (
      /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]|:thumbsup:|:heart:|:thumbsdown:|:smile:|:\)|\<3|:D)/.test(
        message
      ) ||
      message.includes("👍") ||
      message.includes("❤️") ||
      message.includes("👎") ||
      message.includes("😊")
    );
  };

  const handleEmojiMessage = (message: string) => {
    // Process emoji responses
    if (
      message.includes("👍") ||
      message.includes(":thumbsup:") ||
      message.includes("👌")
    ) {
      updateUserPreference("satisfaction", "positive");
      botResponse(
        "I'm glad you like it! Is there anything specific you'd like to know more about?"
      );
    } else if (
      message.includes("❤️") ||
      message.includes("<3") ||
      message.includes(":heart:")
    ) {
      updateUserPreference("satisfaction", "positive");
      botResponse(
        "Thank you for the love! It's great to know you're enjoying our conversation. How else can I assist you today?"
      );
    } else if (message.includes("👎") || message.includes(":thumbsdown:")) {
      updateUserPreference("satisfaction", "negative");
      botResponse(
        "I'm sorry to hear that. Could you tell me what you're looking for, so I can better assist you?"
      );
    } else if (
      message.includes("😊") ||
      message.includes(":)") ||
      message.includes(":D")
    ) {
      updateUserPreference("satisfaction", "positive");
      botResponse(
        "I'm happy you're smiling! What aspect of our AI solutions would you like to explore next?"
      );
    } else {
      botResponse("I see you sent an emoji! How can I help you today?");
    }
  };

  const handleReaction = (messageIndex: number, reaction: string) => {
    // Add reaction to the specified message
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      const message = updatedMessages[messageIndex];

      if (!message.reactions) {
        message.reactions = [];
      }

      // Toggle reaction
      if (message.reactions.includes(reaction)) {
        message.reactions = message.reactions.filter((r) => r !== reaction);
      } else {
        message.reactions.push(reaction);
      }

      return updatedMessages;
    });

    // Update user preferences based on reaction
    if (reaction === "👍" || reaction === "❤️") {
      const message = messages[messageIndex];
      if (message.sender === "bot") {
        // Extract topic from bot message for preference tracking
        let topic = "general";
        if (message.text.toLowerCase().includes("workflow")) topic = "workflow";
        else if (message.text.toLowerCase().includes("automation"))
          topic = "automation";
        else if (message.text.toLowerCase().includes("schedule"))
          topic = "scheduling";

        updateUserPreference(topic, "positive");

        // Provide response to reaction
        if (messageIndex === messages.length - 1) {
          botResponse(
            "Thanks for the positive feedback! Is there anything specific you'd like to know more about?"
          );
        }
      }
    } else if (reaction === "👎") {
      const message = messages[messageIndex];
      if (message.sender === "bot") {
        // Extract topic from bot message
        let topic = "general";
        if (message.text.toLowerCase().includes("workflow")) topic = "workflow";
        else if (message.text.toLowerCase().includes("automation"))
          topic = "automation";
        else if (message.text.toLowerCase().includes("schedule"))
          topic = "scheduling";

        updateUserPreference(topic, "negative");

        // Provide response to negative reaction if it's the last message
        if (messageIndex === messages.length - 1) {
          botResponse(
            "I'm sorry that wasn't helpful. Could you tell me what you're looking for so I can better assist you?"
          );
        }
      }
    }

    setSelectedMessageIndex(null);
  };

  const updateUserPreference = (
    topic: string,
    sentiment: "positive" | "negative" | "neutral"
  ) => {
    setUserPreferences((prev) => {
      const existingIndex = prev.findIndex((p) => p.topic === topic);
      const updated = [...prev];

      if (existingIndex >= 0) {
        // Update existing preference
        const existing = updated[existingIndex];
        updated[existingIndex] = {
          ...existing,
          sentiment: sentiment,
          frequency: existing.frequency + 1,
        };
      } else {
        // Add new preference
        updated.push({
          topic,
          sentiment,
          frequency: 1,
        });
      }

      // Save to localStorage
      localStorage.setItem(
        "innovateAI_userPreferences",
        JSON.stringify(updated)
      );
      return updated;
    });
  };

  const getTopPreferences = (count: number): string[] => {
    return userPreferences
      .filter((pref) => pref.sentiment === "positive")
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, count)
      .map((pref) => pref.topic);
  };

  const askForName = () => {
    botResponse(
      "Before we continue, could you please share your name so I can address you properly?"
    );
    setCollectingName(true);
  };

  const handleNameInput = (name: string) => {
    // Basic validation - name should only contain letters and spaces
    if (!/^[a-zA-Z\s]+$/.test(name)) {
      botResponse(
        "I'm sorry, but there might be a mistake in the name you provided. Could you please share your full name using only letters?"
      );
      return;
    }

    setContactFormData((prev) => ({ ...prev, name }));
    botResponse(
      `Nice to meet you, ${name}! Could you also provide your email address so I can follow up with more information?`
    );
    setCollectingName(false);
    setCollectingEmail(true);
  };

  const handleEmailInput = (email: string) => {
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      botResponse(
        "It seems like the email address you entered might not be valid. Could you please double-check and provide a correct email address?"
      );
      return;
    }

    setContactFormData((prev) => ({ ...prev, email }));
    botResponse(
      `Thanks! Is there a phone number you'd prefer us to use for further communication? (You can type 'skip' if you'd prefer not to share)`
    );
    setCollectingEmail(false);
    setCollectingPhone(true);
  };

  const handlePhoneInput = (phone: string) => {
    if (phone.toLowerCase() === "skip") {
      saveUserData(contactFormData.name, contactFormData.email);
      botResponse(
        `Perfect, ${contactFormData.name}! I've noted your contact details. Would you like to schedule a demo call with our team to discuss how our AI solutions can help your business?`
      );
    } else {
      // Basic phone validation
      if (!/^[+\d\s()-]{7,15}$/.test(phone)) {
        botResponse(
          "It looks like the phone number you entered might not be valid. Please provide a correct phone number or type 'skip' to continue without it."
        );
        return;
      }

      saveUserData(contactFormData.name, contactFormData.email, phone);
      botResponse(
        `Thanks for providing your details, ${contactFormData.name}! Would you like to schedule a demo call with our team to discuss how our AI solutions can help your business?`
      );
    }
    setCollectingPhone(false);
  };

  const saveUserData = (name: string, email: string, phone?: string) => {
    const newUserData = {
      name,
      email,
      phone,
      timestamp: new Date().toISOString(),
    };

    setUserData(newUserData);

    // In a real implementation, you would save this data to a CSV or JSON file
    console.log("User data collected:", newUserData);

    // Example of how you might store this in localStorage for demo purposes
    localStorage.setItem("innovateAI_userData", JSON.stringify(newUserData));
  };

  const botResponse = (text: string) => {
    const newMessage = {
      text,
      sender: "bot" as const,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
    setLoading(false);

    // Automatically speak the bot's response
    if (!voiceState.error) {
      speakText(text);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleFaqClick = (faq: string) => {
    if (faq.toLowerCase().includes("schedule")) {
      window.open(calendlyUrl, "_blank");
      botResponse(
        "You can schedule a meeting with our team using the link I just opened. Once scheduled, please let me know and I'll be happy to help you with any other questions!"
      );
    } else {
      setCurrentMessage(faq);
      handleSendMessage();
    }
  };

  const toggleContactForm = () => {
    setShowContactForm(!showContactForm);
  };

  const handleContactFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveUserData(
      contactFormData.name,
      contactFormData.email,
      contactFormData.phone
    );
    setShowContactForm(false);
    botResponse(
      `Thanks for providing your details, ${contactFormData.name}! Would you like to schedule a call to learn more about our AI solutions?`
    );
  };

  // Function to check if text is English
  const isEnglish = (text: string): boolean => {
    // More comprehensive regex for English text
    const englishRegex = /^[A-Za-z0-9\s.,!?'"()\-:;@#$%&*+=\/\\]+$/;

    // Check if the text contains mostly English characters
    const englishCharCount = text.replace(/[^A-Za-z\s]/g, "").length;
    const totalCharCount = text.replace(/\s/g, "").length;

    // Text should match the regex pattern and contain at least 60% English characters
    return englishRegex.test(text) && englishCharCount / totalCharCount >= 0.6;
  };

  // Function to start voice recording
  const startRecording = () => {
    if (!voiceState.isSupported) {
      setVoiceState((prev) => ({
        ...prev,
        error: "Voice recognition is not supported in your browser.",
      }));
      return;
    }

    try {
      // Ensure any ongoing speech is stopped before recording
      if (synthRef.current?.speaking) {
        synthRef.current.cancel();
      }

      // Reset any previous errors
      setVoiceState((prev) => ({
        ...prev,
        error: null,
        isRecording: true,
      }));

      recognitionRef.current.start();
    } catch (error) {
      console.error("Error starting voice recording:", error);
      setVoiceState((prev) => ({
        ...prev,
        error: "Error starting voice recording. Please try again.",
        isRecording: false,
      }));
    }
  };

  // Function to stop voice recording
  const stopRecording = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error("Error stopping recording:", error);
      }
    }
    setVoiceState((prev) => ({
      ...prev,
      isRecording: false,
      error: null,
    }));
  };

  // Function to speak text
  const speakText = (text: string, messageId?: number) => {
    if (!synthRef.current) {
      setVoiceState((prev) => ({
        ...prev,
        error: "Text-to-speech is not supported in your browser.",
        isSpeaking: false,
      }));
      return;
    }

    try {
      // Cancel any ongoing speech
      synthRef.current.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "en-US";
      utterance.rate = 0.9; // Slightly slower for better clarity
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      // Get available voices and select a good English voice
      const voices = synthRef.current.getVoices();
      const englishVoices = voices.filter(
        (voice) =>
          voice.lang.includes("en-") &&
          (voice.name.includes("Google") || voice.name.includes("Microsoft"))
      );

      if (englishVoices.length > 0) {
        utterance.voice = englishVoices[0];
      }

      utterance.onstart = () => {
        setVoiceState((prev) => ({ ...prev, isSpeaking: true, error: null }));
        if (messageId !== undefined) {
          setPlayingMessage({ messageId, isPlaying: true });
        }
      };

      utterance.onend = () => {
        setVoiceState((prev) => ({ ...prev, isSpeaking: false, error: null }));
        if (messageId !== undefined) {
          setPlayingMessage(null);
        }
      };

      utterance.onerror = (event) => {
        console.error("Speech synthesis error:", event);
        const errorMessage =
          event.error === "interrupted"
            ? "Speech was interrupted."
            : "Error playing voice. Please try again.";

        setVoiceState((prev) => ({
          ...prev,
          isSpeaking: false,
          error: errorMessage,
        }));
        if (messageId !== undefined) {
          setPlayingMessage(null);
        }
      };

      synthRef.current.speak(utterance);
    } catch (error) {
      console.error("Text-to-speech error:", error);
      setVoiceState((prev) => ({
        ...prev,
        isSpeaking: false,
        error: "Failed to play voice. Please try again.",
      }));
      if (messageId !== undefined) {
        setPlayingMessage(null);
      }
    }
  };

  // Function to stop speaking
  const stopSpeaking = () => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setVoiceState((prev) => ({ ...prev, isSpeaking: false }));
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50" ref={chatContainerRef}>
      {/* Floating bubble when chat is minimized */}
      <div
        className={`flex items-center cursor-pointer transition-all duration-300 transform ${
          isExpanded && !isMinimized
            ? "scale-0 opacity-0 absolute"
            : "scale-100 opacity-100"
        }`}
        onClick={toggleChat}
      >
        <div className="bg-purple-600 text-white p-4 rounded-full shadow-lg hover:bg-purple-700 transition-colors flex items-center">
          <MessageCircle className="w-6 h-6 mr-2" />
          <span className="font-medium">Chat with AI</span>
        </div>
      </div>

      {/* Expanded chat interface */}
      <div
        className={`bg-gray-800 rounded-lg shadow-xl overflow-hidden transition-all duration-300 transform origin-bottom-right
          ${
            isExpanded && !isMinimized
              ? "scale-100 opacity-100"
              : "scale-0 opacity-0 absolute pointer-events-none"
          }`}
        style={{
          width: "350px",
          maxWidth: "calc(100vw - 40px)",
          height: "600px",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Chat header */}
        <div className="bg-gray-900 border-b border-purple-700">
          {/* Main header with status and controls */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-5 py-4 flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Brain className="w-7 h-7 text-white" />
              <div className="flex flex-col">
                <h2 className="text-white font-semibold text-lg leading-tight">
                  {companyName}
                </h2>
                <div className="flex items-center mt-0.5">
                  <Circle className="w-2 h-2 fill-green-400 text-green-400 mr-1.5" />
                  <span className="text-green-300 text-sm">
                    Online Assistant
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <a
                href={calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-200 hover:text-white focus:outline-none bg-gray-700/30 hover:bg-gray-700/50 rounded-full p-2 transition-colors"
                title="Schedule a meeting"
                onClick={() => {
                  botResponse(
                    "You can schedule a meeting with our team using this link. Once scheduled, please let me know and I'll be happy to help you with any other questions!"
                  );
                }}
              >
                <CalendarDays className="w-5 h-5" />
              </a>
              <button
                onClick={() => {
                  setIsExpanded(false);
                  setIsMinimized(true);
                }}
                className="text-gray-200 hover:text-white focus:outline-none bg-gray-700/30 hover:bg-gray-700/50 rounded-full p-2 transition-colors"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Chat content wrapper */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Chat messages */}
          <div
            className="flex-1 overflow-y-auto p-3 space-y-3 bg-gradient-to-b from-gray-800/80 to-gray-900/90"
            style={{ scrollBehavior: "smooth" }}
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${
                  message.sender === "user" ? "justify-end" : "justify-start"
                }`}
                onMouseEnter={() => setSelectedMessageIndex(index)}
                onMouseLeave={() => setSelectedMessageIndex(null)}
              >
                {message.sender === "bot" && (
                  <div className="bg-indigo-500 text-white rounded-full w-8 h-8 flex-shrink-0 flex items-center justify-center mr-2">
                    <Brain className="w-4 h-4" />
                  </div>
                )}
                <div className="max-w-[75%] flex flex-col relative">
                  <div
                    className={`rounded-2xl px-3 py-2 ${
                      message.sender === "user"
                        ? "bg-purple-600 text-white rounded-tr-none"
                        : "bg-gray-700 text-gray-100 rounded-tl-none"
                    }`}
                  >
                    <div className="flex items-start">
                      <span className="flex-grow">{message.text}</span>
                      {message.sender === "bot" && (
                        <button
                          onClick={() => {
                            if (playingMessage?.messageId === index) {
                              stopSpeaking();
                              setPlayingMessage(null);
                            } else {
                              if (playingMessage) {
                                stopSpeaking();
                              }
                              speakText(message.text);
                              setPlayingMessage({
                                messageId: index,
                                isPlaying: true,
                              });
                            }
                          }}
                          className="ml-2 p-1 hover:bg-gray-600/50 rounded-full transition-colors"
                          title={
                            playingMessage?.messageId === index
                              ? "Stop speaking"
                              : "Listen to message"
                          }
                        >
                          {playingMessage?.messageId === index ? (
                            <VolumeX className="w-3 h-3 text-gray-300" />
                          ) : (
                            <Volume2 className="w-3 h-3 text-gray-300" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-1">
                    <div className="flex space-x-1">
                      {/* Message reactions */}
                      {message.reactions && message.reactions.length > 0 && (
                        <div className="flex space-x-1">
                          {message.reactions.map((reaction, i) => (
                            <span
                              key={i}
                              className="bg-gray-800 rounded-full px-1.5 py-0.5 text-xs"
                            >
                              {reaction}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Reaction options */}
                      {selectedMessageIndex === index && (
                        <div className="flex space-x-1 bg-gray-800 rounded-full p-1 shadow-lg ml-2">
                          <button
                            onClick={() => handleReaction(index, "👍")}
                            className="hover:bg-gray-700 rounded-full p-1"
                            aria-label="Thumbs Up"
                          >
                            <span role="img" aria-label="Thumbs Up">
                              👍
                            </span>
                          </button>
                          <button
                            onClick={() => handleReaction(index, "❤️")}
                            className="hover:bg-gray-700 rounded-full p-1"
                            aria-label="Heart"
                          >
                            <span role="img" aria-label="Heart">
                              ❤️
                            </span>
                          </button>
                          <button
                            onClick={() => handleReaction(index, "👎")}
                            className="hover:bg-gray-700 rounded-full p-1"
                            aria-label="Thumbs Down"
                          >
                            <span role="img" aria-label="Thumbs Down">
                              👎
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatTime(message.timestamp)}
                    </span>
                  </div>
                </div>
                {message.sender === "user" && (
                  <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex-shrink-0 flex items-center justify-center ml-2">
                    <span className="text-sm font-semibold">
                      {userData?.name?.charAt(0) || "U"}
                    </span>
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-indigo-500 text-white rounded-full w-8 h-8 flex-shrink-0 flex items-center justify-center mr-2">
                  <Brain className="w-4 h-4" />
                </div>
                <div className="max-w-[75%]">
                  <div className="bg-gray-700 text-white rounded-2xl rounded-tl-none px-4 py-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.4s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick FAQs - Made sticky */}
          <div className="sticky bottom-0 z-10 p-2 bg-gray-900 border-t border-gray-700 overflow-x-auto whitespace-nowrap flex space-x-2">
            {quickFaqs.map((faq, index) => (
              <button
                key={index}
                onClick={() => handleFaqClick(faq)}
                className={`text-xs ${
                  faq.includes("Schedule")
                    ? "bg-purple-700 hover:bg-purple-600"
                    : "bg-gray-800 hover:bg-gray-700"
                } text-gray-300 px-3 py-1 rounded-full whitespace-nowrap transition-colors flex-shrink-0`}
              >
                {faq.includes("Schedule") && (
                  <Calendar className="w-3 h-3 inline mr-1" />
                )}
                {faq}
              </button>
            ))}
          </div>

          {/* Chat input - Made sticky */}
          <div className="sticky bottom-0 z-20 p-2 bg-gray-900 border-t border-gray-700 flex flex-col">
            <div className="flex items-center space-x-2">
              <button
                onClick={
                  voiceState.isRecording ? stopRecording : startRecording
                }
                className={`p-2 rounded-full transition-colors ${
                  voiceState.isRecording
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-gray-700 hover:bg-gray-600"
                }`}
                title={
                  voiceState.isRecording ? "Stop Recording" : "Start Recording"
                }
              >
                <Mic
                  className={`w-4 h-4 ${
                    voiceState.isRecording ? "animate-pulse" : ""
                  }`}
                />
              </button>

              <div className="flex-1 relative">
                <input
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    voiceState.isRecording
                      ? "Listening..."
                      : "Type your message in English..."
                  }
                  className="w-full bg-gray-800 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
                {voiceState.isRecording && (
                  <div className="absolute left-0 right-0 -top-6 text-xs text-green-400 text-center">
                    Listening...
                  </div>
                )}
              </div>

              <button
                onClick={
                  voiceState.isSpeaking
                    ? stopSpeaking
                    : () => speakText(messages[messages.length - 1]?.text)
                }
                className={`p-2 rounded-full transition-colors ${
                  voiceState.isSpeaking
                    ? "bg-purple-600 hover:bg-purple-700"
                    : "bg-gray-700 hover:bg-gray-600"
                }`}
                title={
                  voiceState.isSpeaking ? "Stop Speaking" : "Play Last Message"
                }
              >
                {voiceState.isSpeaking ? (
                  <VolumeX className="w-4 h-4" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
              </button>

              <button
                onClick={handleSendMessage}
                className="bg-purple-600 hover:bg-purple-700 text-white p-2 rounded-lg transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>

            {/* Voice error message with improved styling */}
            {voiceState.error && (
              <div className="mt-2 text-xs text-red-400 text-center bg-red-900/20 py-1 px-2 rounded">
                {voiceState.error}
              </div>
            )}

            {/* Reaction buttons */}
            <div className="flex justify-center space-x-3 mt-2">
              <button
                onClick={() => {
                  setCurrentMessage("👍");
                  handleSendMessage();
                }}
                className="text-gray-400 hover:text-gray-200 focus:outline-none"
                aria-label="Send thumbs up"
              >
                <ThumbsUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setCurrentMessage("❤️");
                  handleSendMessage();
                }}
                className="text-gray-400 hover:text-gray-200 focus:outline-none"
                aria-label="Send heart"
              >
                <Heart className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setCurrentMessage("👎");
                  handleSendMessage();
                }}
                className="text-gray-400 hover:text-gray-200 focus:outline-none"
                aria-label="Send thumbs down"
              >
                <ThumbsDown className="w-4 h-4" />
              </button>
            </div>

            {/* Contact form toggle */}
            <div className="mt-2 text-center">
              <button
                onClick={toggleContactForm}
                className="text-purple-400 hover:text-purple-300 text-xs"
              >
                {showContactForm
                  ? "Hide contact form"
                  : "Provide your contact info"}
              </button>
            </div>
          </div>
        </div>

        {/* Contact form */}
        {showContactForm && (
          <div className="absolute bottom-16 left-0 right-0 bg-gray-800 p-3 rounded-t-lg shadow-lg border border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-purple-300 text-xs font-medium">
                Your Contact Details
              </h4>
              <button onClick={toggleContactForm}>
                <X className="w-4 h-4 text-gray-400 hover:text-gray-300" />
              </button>
            </div>
            <form onSubmit={handleContactFormSubmit} className="space-y-2">
              <div>
                <input
                  type="text"
                  name="name"
                  value={contactFormData.name}
                  onChange={handleContactFormChange}
                  placeholder="Your name"
                  className="w-full bg-gray-700 rounded px-3 py-1.5 text-white text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                  required
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={contactFormData.email}
                  onChange={handleContactFormChange}
                  placeholder="Your email"
                  className="w-full bg-gray-700 rounded px-3 py-1.5 text-white text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                  required
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={contactFormData.phone}
                  onChange={handleContactFormChange}
                  placeholder="Your phone (optional)"
                  className="w-full bg-gray-700 rounded px-3 py-1.5 text-white text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-1.5 rounded text-xs transition-colors"
              >
                Submit
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingChat;
